import { Box, Center, Flex, Image, Link, Spinner, Text } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { MouseEvent, useEffect, useState } from 'react';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../../auth/keycloak';
import config from '../../config';
import {
    getActivePlayerCount,
    getGameIcon,
    getStreamUrl,
    getViewerCount,
} from '../../service/games';
import { GameInfo, getGameName, getStreamInfo, getUserName } from '../../service/lobby';
import { isLive, UserProfile } from '../../service/social';
import { AvatarImage } from '../profileComponents/AvatarImage';
import { FOLLOWING_COLOR, OFFLINE_COLOR, ONLINE_COLOR } from '../styles';
import { LiveSocialLinks } from './LiveSocialLinks';
import { NotificationToggle } from './NotificationToggle';
import { OfflineBadge, PlayerBadge, ViewerBadge } from './ViewerBadge';

export interface LiveSixListItemProps {
    gameInfo?: GameInfo;
    profile: UserProfile | undefined;
    index: number;
    displayNotifications?: boolean;
    following?: UserProfile[] | undefined;
}

export interface ListItemDisplayData {
    onlineStatus: boolean;
    usernameText: string;
    streamUrl: string;
    streamSixID: string;
    avatarImageUrl: string;
    titleText: string;
    descriptionText: string;
    viewCount?: number;
    following?: boolean;
    activePlayerCount?: number;
    streamingStatus: boolean;
}

export const convertToDisplayData = async (
    profile: UserProfile,
    gameInfo?: GameInfo,
): Promise<ListItemDisplayData> => {
    let usernameText = '';
    let avatarUrl = '';
    let titleText = '';
    let descriptionText = '';
    let id = '';
    let onlineStatus = false;
    let streamingStatus = false;
    const viewCount = getViewerCount(profile);
    let activePlayerCount: number | undefined;

    if (!profile) {
        return {
            onlineStatus: onlineStatus,
            avatarImageUrl: avatarUrl,
            streamUrl: '',
            streamSixID: id,
            usernameText: usernameText,
            titleText: titleText,
            descriptionText: descriptionText,
            viewCount: viewCount,
            streamingStatus: streamingStatus,
        };
    }

    if (profile.avatar_object_url) {
        avatarUrl = profile.avatar_object_url;
    } else if (profile.twitch_profile) {
        avatarUrl = profile.twitch_profile?.profile_image_url;
    }
    id = profile.id;
    descriptionText = profile.description;

    const { isUserLive: isUserStreaming, gameInfo: returnedGameInfo } = await isLive(
        profile,
        gameInfo,
    );
    onlineStatus = !!gameInfo;
    gameInfo = gameInfo ? gameInfo : returnedGameInfo;
    streamingStatus = isUserStreaming;

    usernameText = getUserName(profile);

    if (gameInfo !== undefined) {
        activePlayerCount = getActivePlayerCount(gameInfo);
        titleText = getGameName(gameInfo.license);
        if (descriptionText === '') {
            descriptionText = getStreamInfo(gameInfo, profile);
        }
    }

    if (avatarUrl === '') {
        avatarUrl = config.avatarPlaceholderURL;
    }

    const streamUrl = getStreamUrl(profile.twitch_profile?.login);

    return {
        onlineStatus: onlineStatus,
        streamingStatus: streamingStatus,
        avatarImageUrl: avatarUrl,
        streamUrl: streamUrl,
        streamSixID: id,
        usernameText: usernameText,
        titleText: titleText,
        descriptionText: descriptionText,
        viewCount: viewCount,
        activePlayerCount: activePlayerCount,
    };
};

export const LiveSixListItem: React.FC<LiveSixListItemProps> = ({
    gameInfo,
    profile,
    index,
    displayNotifications = true,
    following,
}) => {
    const [profileToDisplay, setProfileToDisplay] = useState(gameInfo?.userProfile);
    const [displayData, setDisplayData] = useState<ListItemDisplayData>();
    const [isLoading, setIsLoading] = useState(true);
    const [followingUser, setFollowingUser] = useState<boolean | undefined>();
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();

    const renderGameIcon = () => {
        if (gameInfo) {
            return (
                <Image
                    src={getGameIcon(gameInfo?.license)}
                    className="play-icon"
                    onClick={(e: MouseEvent) => {
                        if (gameInfo) {
                            navigate(`/game/${gameInfo?.license}`, {
                                replace: true,
                            });
                            e.preventDefault();
                            return false;
                        }
                    }}
                    border={`2px solid ${followingUser ? FOLLOWING_COLOR : ONLINE_COLOR}`}
                />
            );
        }
    };

    const renderStreamIcons = () => {
        if (displayData?.streamingStatus && profile) {
            return (
                <Flex className={`live-six-social-links ${followingUser && 'following'}`}>
                    <LiveSocialLinks profile={profile} />
                </Flex>
            );
        }
    };

    const renderLiveStats = () => {
        if (displayData?.onlineStatus) {
            return (
                <Flex className={'live-six-viewer-container'}>
                    <ViewerBadge count={displayData.viewCount} width={'46px'} />
                    <PlayerBadge count={displayData.activePlayerCount} width={'58px'} />
                </Flex>
            );
        } else {
            return (
                <Flex className={'live-six-viewer-container'}>
                    <OfflineBadge />
                </Flex>
            );
        }
    };

    useEffect(() => {
        setProfileToDisplay(profile || gameInfo?.userProfile);
    }, [gameInfo, profile]);

    useEffect(() => {
        const loadData = async () => {
            if (!profileToDisplay) {
                console.log(
                    `[LiveSixListItem] profile is null at index: ${index} owner: ${gameInfo?.owner}`,
                );
                return;
            }

            let localDisplayData = await convertToDisplayData(profileToDisplay, gameInfo);
            setDisplayData(localDisplayData);
            if (localDisplayData && localDisplayData.onlineStatus) {
                if (following) {
                    const isFollowing = following?.some(
                        (user) => user.id === localDisplayData.streamSixID,
                    );
                    setFollowingUser(isFollowing);
                }
            }
            setIsLoading(false);
        };

        loadData();
    }, [gameInfo, profileToDisplay, following, index]);

    return !profileToDisplay ? null : (
        <>
            <Box className={`${isMobile ? 'current-six-box-mobile' : 'current-six-box'}`}>
                <Flex className={'live-six-parent'}>
                    {isLoading ? (
                        <Center>
                            <Spinner data-cy="loading" />
                        </Center>
                    ) : (
                        displayData && (
                            <Link
                                as={ReactRouterLink}
                                to={`/user/${displayData.streamSixID}`}
                                state={displayData}
                                className={'live-six'}
                            >
                                <MobileView>
                                    {displayData.onlineStatus ? (
                                        <>
                                            <AvatarImage
                                                src={displayData.avatarImageUrl}
                                                className={`profile-picture`}
                                            />
                                            {renderGameIcon()}
                                            {renderStreamIcons()}
                                            {renderLiveStats()}
                                        </>
                                    ) : (
                                        <>
                                            <AvatarImage
                                                src={displayData.avatarImageUrl}
                                                className={`profile-picture`}
                                            />
                                            {renderLiveStats()}
                                        </>
                                    )}
                                </MobileView>
                                <BrowserView>
                                    {displayData.onlineStatus ? (
                                        <>
                                            <AvatarImage
                                                src={displayData.avatarImageUrl}
                                                className={`profile-picture`}
                                            />
                                            {renderGameIcon()}
                                            <Flex className={'browser-stats'}>
                                                {renderLiveStats()}
                                                {renderStreamIcons()}
                                            </Flex>
                                        </>
                                    ) : (
                                        <>
                                            <AvatarImage
                                                src={displayData.avatarImageUrl}
                                                className={`profile-picture`}
                                            />
                                            {renderLiveStats()}
                                        </>
                                    )}
                                </BrowserView>
                                <Box
                                    className={`username-text`}
                                    background={
                                        displayData.onlineStatus
                                            ? followingUser
                                                ? FOLLOWING_COLOR
                                                : ONLINE_COLOR
                                            : OFFLINE_COLOR
                                    }
                                >
                                    <Text noOfLines={1}>{displayData.usernameText}</Text>
                                </Box>
                                <Flex className={'six-list-item-content'}>
                                    <Flex className="text-holder">
                                        {displayData.titleText && (
                                            <Text className={'game-name-text'} noOfLines={1}>
                                                {displayData.titleText}
                                            </Text>
                                        )}
                                        {displayData.descriptionText && (
                                            <Text noOfLines={3} className={'stream-info-text'}>
                                                {displayData.descriptionText}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                                {displayNotifications && isAuthenticated(keycloak) && (
                                    <Box className="live-six-notification">
                                        <NotificationToggle
                                            streamSixIDText={displayData.streamSixID}
                                            keycloak={keycloak}
                                        />
                                    </Box>
                                )}
                            </Link>
                        )
                    )}
                </Flex>
            </Box>
        </>
    );
};
